import type {
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from 'fhir/r4';
import moment from 'moment';
import { useState, useCallback } from 'react';

import { EXCLUDED_CHART_GROUPS } from './constants';
import {
  isAddendumQuestionnaire,
  isRdConsultChart,
  getOriginalChartId,
  getChainParentId,
} from './fhir-helpers';
import type { LooseQuestionnaireItem } from './fhir-helpers';

/** Insert position: chart items go before all addendum items so addendum fields stay grouped. */
const CHART_INSERT_INDEX = 0;

/**
 * A `/Questionnaire` or `/QuestionnaireResponse` search bundle as this hook
 * reads it: only `entry[].resource`, and `resource` is required because every
 * `.find`/`.filter` callback here dereferences it unguarded.
 */
type SearchBundleOf<T> = { entry?: { resource: T }[] | undefined } | undefined;

/**
 * A `Questionnaire` with the chart's item groups spliced into its own `item`.
 *
 * `item` is REQUIRED on the result even though it is `0..1` on the source: the
 * producers below build it by spreading `addendumQ.item`, which throws on an
 * absent one, so by the time a value exists it has an array.
 */
type InjectedQuestionnaire = Questionnaire & { item: LooseQuestionnaireItem[] };

interface AddendumStateReturn {
  originalChartId: string | null;
  chainParentId: string | null;
  clearAddendumState: () => void;
  /** Creates an addendum with chart items injected flat, returns questionnaire + prefill QR. */
  prepareAddendum: (
    activeItem: string,
    questionnaireData: SearchBundleOf<Questionnaire>,
    responseData: SearchBundleOf<QuestionnaireResponse>,
    findResponseData: (id: string) => { resource: QuestionnaireResponse } | undefined
  ) => {
    questionnaire: InjectedQuestionnaire;
    /** Only the chart items, to seed a new response; `undefined` when there are none. */
    prefillResponse: { item: QuestionnaireResponseItem[] } | undefined;
  } | null;
  /** Re-injects chart item groups into a questionnaire when editing an addendum. */
  prepareEditQuestionnaire: (
    qResponse: QuestionnaireResponse,
    questionnaire: Questionnaire | undefined,
    questionnaireData: SearchBundleOf<Questionnaire>
  ) => Questionnaire | undefined;
  /** Reads and sets addendum IDs from a QR being edited. */
  setStateFromResponse: (qResponse: QuestionnaireResponse) => void;
}

/**
 * Extracts chart-related items from a QR's `.item` array by matching linkIds
 * against the RD consult chart questionnaire definition.
 */
function extractChartItems(
  qrItems: QuestionnaireResponseItem[] | undefined,
  chartQ: Questionnaire | undefined
): QuestionnaireResponseItem[] {
  if (!qrItems || !chartQ?.item) return [];
  const chartLinkIds = new Set(
    chartQ.item.filter((g) => !EXCLUDED_CHART_GROUPS.has(g.linkId)).map((g) => g.linkId)
  );
  return qrItems.filter((item) => chartLinkIds.has(item.linkId));
}

/**
 * Manages addendum linking state and provides helpers for creating/editing
 * addendum questionnaires with flat chart item injection.
 */
export function useAddendumState(): AddendumStateReturn {
  const [originalChartId, setOriginalChartId] = useState<string | null>(null);
  const [chainParentId, setChainParentId] = useState<string | null>(null);

  const clearAddendumState = useCallback(() => {
    setOriginalChartId(null);
    setChainParentId(null);
  }, []);

  const setStateFromResponse = useCallback((qResponse: QuestionnaireResponse) => {
    const ogId = getOriginalChartId(qResponse.extension);
    setOriginalChartId(ogId);
    setChainParentId(ogId ? getChainParentId(qResponse.extension) : null);
  }, []);

  const prepareAddendum = useCallback((
    activeItem: string,
    questionnaireData: SearchBundleOf<Questionnaire>,
    responseData: SearchBundleOf<QuestionnaireResponse>,
    findResponseData: (id: string) => { resource: QuestionnaireResponse } | undefined
  ): {
    questionnaire: InjectedQuestionnaire;
    prefillResponse: { item: QuestionnaireResponseItem[] } | undefined;
  } | null => {
    // `Questionnaire.id` is `0..1`; `isAddendumQuestionnaire` takes a string and
    // only does an `includes`, so an id-less questionnaire simply never matches.
    const addendumQ = questionnaireData?.entry?.find((q) =>
      isAddendumQuestionnaire(q.resource.id!)
    )?.resource;
    if (!addendumQ) return null;

    const activeRes = findResponseData(activeItem)?.resource;
    const activeOgChartId = activeRes ? getOriginalChartId(activeRes.extension) : null;
    const resolvedOgChartId = activeOgChartId ?? activeItem;

    const existingAddenda = responseData?.entry
      ?.filter((qr) => {
        const qrOgId = getOriginalChartId(qr.resource.extension);
        return qrOgId === resolvedOgChartId && qr.resource.status === 'completed';
      })
      ?.sort((a, b) => moment(b.resource.authored).valueOf() - moment(a.resource.authored).valueOf());

    // `existingAddenda` is `undefined` when `responseData?.entry` is absent, and
    // `undefined > 0` is false — the same accidentally-correct emptiness test
    // this subtree uses elsewhere. `[0]` is then in range by that test.
    const latestInChain = (existingAddenda?.length as number) > 0 ? existingAddenda![0]!.resource : null;
    const chainSource = latestInChain ?? findResponseData(resolvedOgChartId)?.resource;

    // `questionnaireData.entry` is asserted, not optional-chained: this read is
    // unguarded today and throws on a bundle with no `entry`, whereas the
    // `addendumQ` lookup above uses `?.`. The two disagree, and the throw is
    // reachable only if a `/Questionnaire` search returns an addendum but no
    // chart — which cannot happen, since the addendum lookup would have
    // returned null first.
    const originalChartQ = questionnaireData.entry!.find((q) =>
      isRdConsultChart(q.resource.id!)
    )?.resource;
    if (!originalChartQ) return null;

    const chartItemDefs = (originalChartQ.item ?? []).filter(
      (g) => !EXCLUDED_CHART_GROUPS.has(g.linkId)
    );

    // `Questionnaire.item` is `0..1`; spreading an absent one throws here today.
    const addendumItems = [...addendumQ.item!];
    addendumItems.splice(CHART_INSERT_INDEX, 0, ...chartItemDefs);

    const injectedQuestionnaire = {
      ...addendumQ,
      item: addendumItems,
    };

    let prefillItems: QuestionnaireResponseItem[] = [];
    if (latestInChain) {
      prefillItems = extractChartItems(latestInChain.item, originalChartQ);
    } else if (chainSource?.item) {
      prefillItems = extractChartItems(chainSource.item, originalChartQ);
    }

    const prefillResponse = prefillItems.length > 0
      ? { item: prefillItems }
      : undefined;

    setOriginalChartId(resolvedOgChartId);
    setChainParentId(chainSource?.id ?? resolvedOgChartId);

    return { questionnaire: injectedQuestionnaire, prefillResponse };
  }, []);

  const prepareEditQuestionnaire = useCallback((
    qResponse: QuestionnaireResponse,
    questionnaire: Questionnaire | undefined,
    questionnaireData: SearchBundleOf<Questionnaire>
  ): Questionnaire | undefined => {
    const ogId = getOriginalChartId(qResponse.extension);
    if (!ogId || !questionnaire || !isAddendumQuestionnaire(questionnaire.id!)) {
      return questionnaire;
    }

    // `entry` asserted for the same reason as in `prepareAddendum`.
    const originalChartQ = questionnaireData!.entry!.find((q) =>
      isRdConsultChart(q.resource.id!)
    )?.resource;
    if (!originalChartQ) return questionnaire;

    const chartItemDefs = (originalChartQ.item ?? []).filter(
      (g) => !EXCLUDED_CHART_GROUPS.has(g.linkId)
    );
    const items = [...questionnaire.item!];
    items.splice(CHART_INSERT_INDEX, 0, ...chartItemDefs);

    return { ...questionnaire, item: items };
  }, []);

  return {
    originalChartId,
    chainParentId,
    clearAddendumState,
    prepareAddendum,
    prepareEditQuestionnaire,
    setStateFromResponse,
  };
}
